import Bookkeeper from "components/company/Bookkeeper";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import { palette } from "lib/miolo";
import formatCnpj from "lib/utils/formatCnpj";
import * as React from "react";

interface IImpostoDeRendaProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment | IBrazilianRealEstateFundTopLevelFragment;
}

function ImpostoDeRenda({ issuer }: IImpostoDeRendaProps) {
  const isBrazilianIssuer = issuer.__typename === "BrazilianCompanyIssuer";
  const isBrazilianRealEstateFundIssuer = issuer.__typename === "BrazilianRealEstateFund";

  return (
    <div className="space-y-4 rounded-xl p-2 py-0 font-aeonik">
      <div className="flex items-center space-x-2">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 squircle">
          <RFBLion className="h-6 w-6" />
        </div>

        <div>
          <h4 className="text-lg font-bold text-blue-700">Imposto de Renda</h4>
          <p className="text-sm font-normal italic text-gray-500">Dados para sua declaração</p>
        </div>
      </div>

      <div className="flex items-center space-x-8 px-2">
        <div className="space-y-2">
          <strong className="text-sm font-bold text-gray-700">CNPJ</strong>
          <div className="text-sm font-normal text-gray-600">{formatCnpj(issuer.cnpj)}</div>
        </div>

        {isBrazilianIssuer && issuer.bookkeeper && (
          <div className="space-y-2">
            <strong className="text-sm font-bold text-gray-700">Escriturador</strong>
            <div>
              <Bookkeeper bookkeeper={issuer.bookkeeper} side="top" />
            </div>
          </div>
        )}

        {isBrazilianRealEstateFundIssuer && issuer.administrator.name && (
          <div className="flex flex-col space-y-2">
            <strong className="text-sm font-bold text-gray-700">Administrador</strong>
            <span className="text-sm font-normal text-gray-600">{issuer.administrator.name}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImpostoDeRenda;

function RFBLion(props: React.SVGProps<any>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={38} height={38} viewBox="0 0 38 38" {...props}>
      <g fill={palette.cobalt}>
        <path d="M26.987 16.432c.29-1.757.62-6.163-2.532-9.142a.836.836 0 00-1.201.043.866.866 0 00.042 1.215c2.55 2.413 2.256 6.119 2.014 7.6-.75 4.564 1.66 8.225 4.165 10.13a.85.85 0 001.192-.173.871.871 0 00-.17-1.206c-2.11-1.602-4.139-4.666-3.51-8.467zm-14.261.723a1.405 1.405 0 00-.88-.904c-1.106-.393-3.01.537-4.264 1.278l-.29-.536c1.194-.719 1.813-1.435 1.977-2.267.138-.697-.168-1.088-.361-1.257-.651-.571-1.62-.214-2.555.126-.276.101-.53.204-.701.229-.153.024-.19-.015-.22-.04-.19-.17-.315-.63-.367-1.061l4.48-2.431a.86.86 0 00.442-.653c.194-1.572.902-2.814 2.103-3.69 2.11-1.54 5.19-1.629 6.744-1.313a.84.84 0 001-.674.858.858 0 00-.664-1.014c-1.595-.326-5.322-.404-8.072 1.603-1.483 1.082-2.4 2.57-2.73 4.425l-4.584 2.487a.87.87 0 00-.447.746c-.005.33.014 2.015.976 2.87.437.387.99.542 1.593.447.224-.033.553-.14.91-.27-.283.2-.64.41-1.064.622a.858.858 0 00-.43.519.861.861 0 00.07.674c.003.003.527.95 1.111 2.072a.848.848 0 00.539.43.83.83 0 00.677-.108c1.322-.867 2.872-1.578 3.447-1.597.373 1.321-.143 2.387-5.136 5.94-2.11 1.5-7.052 5.014-5.842 12.677a.854.854 0 00.975.716.863.863 0 00.705-.988c-.92-5.82 2.082-8.825 5.138-11 5.007-3.559 6.624-5.311 5.72-8.058z" />
        <path d="M36.933 17.381c-3.527.583-5.441-4.354-6.988-8.313-.608-1.557-1.134-2.902-1.718-3.849A10.21 10.21 0 0016.682.34a.862.862 0 00-.553 1.081.846.846 0 001.067.56c.06-.02 5.906-1.822 9.588 4.149.502.814 1.027 2.154 1.582 3.572 1.576 4.028 3.699 9.452 7.966 9.451.28 0 .572-.023.873-.072a.863.863 0 00.705-.988.868.868 0 00-.977-.712zm-14.57-4.361a.851.851 0 00-1.037-.612.862.862 0 00-.607 1.051c.01.04.986 3.927-1.153 8.342-1.456 3.007-2.002 7.914.834 12.667a.845.845 0 001.168.288c.4-.246.53-.773.289-1.183-2.484-4.163-1.962-8.536-.762-11.016 2.417-4.982 1.317-9.353 1.268-9.536zm-5.101 2.036a.865.865 0 00-.69.995c.006.034.524 3.462-3.424 7.881-1.773 1.987-7.175 8.034-4.185 13.466a.854.854 0 00.745.443.858.858 0 00.412-.109.872.872 0 00.33-1.173c-1.613-2.93-.317-6.68 3.961-11.47 4.558-5.105 3.868-9.176 3.836-9.346a.85.85 0 00-.985-.687z" />
      </g>
    </svg>
  );
}
